<template>
  <div class="pt-8">
    <v-row>
      <v-col cols="12" sm="9" md="6" lg="5">
        <v-form ref="form" @submit.stop.prevent>
          <v-text-field
            v-model.trim="editedDealer.name"
            :rules="rules.name"
            :label="$t('dealer.edit.name')"
            tabindex="1"
            clearable
            autofocus
          ></v-text-field>
          <v-text-field
            v-model.trim="editedDealer.phone"
            :rules="rules.phone"
            :label="$t('dealer.edit.phone')"
            type="tel"
            tabindex="1"
            clearable
          ></v-text-field>
          <v-text-field
            v-model.trim="editedDealer.url"
            :rules="rules.url"
            validate-on-blur
            :label="$t('dealer.edit.url')"
            type="url"
            tabindex="1"
            clearable
          ></v-text-field>
          <v-text-field
            v-model.trim="editedDealer.emailAddress"
            :rules="rules.emailAddress"
            validate-on-blur
            :label="$t('dealer.edit.email')"
            type="email"
            tabindex="1"
            clearable
          ></v-text-field>
          <dealer-copy-notifications-fields :dealer="editedDealer"></dealer-copy-notifications-fields>
        </v-form>

        <v-btn
          v-text="$t('save')"
          :disabled="!isModelChanged()"
          class="d-btn-1 text-uppercase mt-8"
          width="120"
          depressed
          @click="saveChanges"
        ></v-btn>
      </v-col>
    </v-row>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'
import { dealerSchema } from '@/constants'

import objectUtil from '@/utils/object.util'
import { ensureCopyNotificationsEmails, prepareDealerCopyNotifications } from '@/utils/copyNotifications.util'

import pageMixin from '@/mixins/page.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'


import DealerCopyNotificationsFields from '@/components/DealerCopyNotificationsFields'


export default {
  components: {
    DealerCopyNotificationsFields
  },


  mixins: [
    pageMixin,
    snackbarMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  data () {
    return {
      editedDealer: {},
      existingNames: []
    }
  },


  computed: {
    ...mapGetters('user', ['currentUser', 'currentUserIsSuperAdmin']),
    ...mapGetters('dealer', ['currentDealerId', 'shortDealerById', 'dealerTableItemById']),

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required(),
          rule.maxLength(dealerSchema.nameMaxLength),
          rule.unique(this.existingNames, this.$t('dealer.edit.nameExists'))
        ],
        phone: [rule.maxLength(dealerSchema.phoneMaxLength)],
        url: [rule.url(), rule.maxLength(dealerSchema.urlMaxLength)],
        emailAddress: [rule.required(), rule.email(), rule.maxLength(dealerSchema.emailMaxLength)]
      }
    }
  },


  methods: {
    ...mapActions('dealer', ['loadDealerById', 'loadDealersTableItems', 'loadShortDealers', 'loadShortDealersByChainId', 'updateDealer', 'updateDealerWithChainId']),

    async loadDealer () {
      try {
        this.startPageLoading()
        const dealerId = this.currentUserIsSuperAdmin ? this.currentDealerId : this.currentUser.dealerId
        if (!dealerId) return

        let chainId
        if (this.currentUserIsSuperAdmin) {
          // сначала ищем в уже загруженных полных объектах (tableItems содержит chainId)
          let fullDealer = this.dealerTableItemById(dealerId)
          if (!fullDealer?.chainId) {
            // если не закешировано — грузим все dealers
            await this.loadDealersTableItems({ pageNumber: 1, pageSize: 1000 })
            fullDealer = this.dealerTableItemById(dealerId)
          }
          chainId = fullDealer?.chainId
        } else {
          chainId = this.shortDealerById(dealerId)?.chainId || this.currentUser.chainId
        }

        if (!chainId) return
        const dealer = await this.loadDealerById({ chainId, id: dealerId })
        this.editedDealer = objectUtil.getObjectCopy(dealer)
        if (typeof this.editedDealer.isCopyNotifications !== 'boolean') {
          this.$set(this.editedDealer, 'isCopyNotifications', false)
        }
        ensureCopyNotificationsEmails(this.editedDealer)
        this.initWatchedModel(this.editedDealer)
        this.resetValidation()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async saveChanges () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        prepareDealerCopyNotifications(this.editedDealer)
        if (this.currentUserIsSuperAdmin) {
          await this.updateDealer(this.editedDealer)
        } else {
          await this.updateDealerWithChainId({ dealer: this.editedDealer, chainId: this.editedDealer.chainId })
        }
        this.initWatchedModel(this.editedDealer)
        // обновляем дропдаун с дилерами
        if (this.currentUserIsSuperAdmin) {
          await this.loadShortDealers()
        } else {
          await this.loadShortDealersByChainId(this.currentUser.chainId)
        }
        this.showSnackbarSuccess(this.$t('template.templatesUpdated'))
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },


  watch: {
    async currentDealerId () {
      if (this.currentUserIsSuperAdmin) {
        await this.loadDealer()
      }
    }
  },


  async created () {
    await this.loadDealer()
  },

  beforeRouteLeave (to, from, next) {
    this.escape(next, () => next(false))
  }
}
</script>
